import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Upload, Button, List, Image, message, Popconfirm, Space } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const MediaLibrary = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const { token } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/media', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load media');
        const data = await res.json();
        setItems(data || []);
      } catch (e) {
        message.error((e && e.message) || 'Failed to load media');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onUpload = async ({ file, onSuccess, onError }) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/media', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        let err = 'Upload failed';
        try { const j = await res.json(); if (j?.error) err = j.error; } catch {}
        throw new Error(err);
      }
      const saved = await res.json();
      setItems(prev => [saved, ...prev]);
      message.success(`${file.name} uploaded`);
      if (onSuccess) onSuccess(saved, file);
    } catch (e) {
      message.error(e?.message || 'Upload failed');
      if (onError) onError(e);
    }
  };

  const onDelete = async (id) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/media/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setItems(prev => prev.filter(it => String(it.id) !== String(id)));
      message.success('Deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Creatives' }, { title: 'Media Library' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Media Library</Title>
        <Upload showUploadList={false} customRequest={onUpload}>
          <Button icon={<UploadOutlined />}>Upload Media</Button>
        </Upload>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Browse and manage your uploaded assets.</Paragraph>
        <List
          loading={loading}
          grid={{ gutter: 16, column: 4 }}
          dataSource={items}
          renderItem={(item) => (
            <List.Item>
              <div>
                <Image src={item.url} alt={item.original_name || 'media'} width={200} height={112} style={{ objectFit: 'cover' }} />
                <div style={{ marginTop: 8, fontSize: 12, color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span title={item.original_name || ''} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.original_name || item.file_name}</span>
                  <Space size={8}>
                    <Popconfirm title="Delete this media?" onConfirm={() => onDelete(item.id)} okText="Delete" okButtonProps={{ danger: true }}>
                      <Button danger size="small">Delete</Button>
                    </Popconfirm>
                  </Space>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

export default MediaLibrary;
